'use client';

import clsx from 'clsx';
import { type ForwardedRef, useImperativeHandle, useRef } from 'react';
import {
  Input as RACInput,
  NumberField as RACNumberField,
  type NumberFieldProps as RACNumberFieldProps,
  Group as RACGroup,
} from 'react-aria-components';
import { forwardRef } from '../../forward-ref.js';
import { useLocalizedMessages } from '../../i18n/index.js';
import { IconAdd, IconSubtract } from '../../icons/index.js';
import { useProductionWarning } from '../../utils/use-production-warning.js';
import { withStyleProps } from '../../with-style-props.js';
import { IconButton } from '../button/icon-button.js';
import { FormFieldDescription, FormFieldError, FormFieldLabel } from '../internal/form-field/form-field.js';
import type { ApiProps, CommonProps, FieldWithPlaceholderProps } from '../types.js';
import { textStyle } from '../typography/utils.js';

export interface NumberFieldProps
  extends
    CommonProps,
    FieldWithPlaceholderProps<number>,
    ApiProps<NumberFieldApi>,
    Pick<
      RACNumberFieldProps,
      | 'isDisabled'
      | 'isRequired'
      | 'isInvalid'
      | 'isReadOnly'
      | 'autoFocus'
      | 'minValue'
      | 'maxValue'
      | 'step'
      | 'formatOptions'
      | 'onFocus'
      | 'onBlur'
      | 'onKeyDown'
    > {
  /** The current value (controlled). A `null` value means an empty field. */
  value?: number | null;
  /** The default value (uncontrolled). A `null` value means an empty field. */
  defaultValue?: number | null;
  /** Handler that is called when the value changes. A `null` value means an empty field. */
  onChange?: (value: number | null) => void;
  /** @internal */
  UNSTABLE_hideSpinner?: boolean;
}

/** The imperative API exposed by the `NumberField` component. */
export interface NumberFieldApi {
  /** Focuses the input field. */
  focus: () => void;
  /** Selects the contents of the input field. */
  select: () => void;
}

function NumberField(
  {
    label,
    description,
    error: errorMessage,
    placeholder,
    UNSAFE_className,
    UNSAFE_style,
    value,
    defaultValue,
    onChange,
    apiRef,
    UNSTABLE_hideSpinner,
    ...props
  }: NumberFieldProps,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const inputRef = useRef<HTMLInputElement>(null);

  const { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy } = props;

  useProductionWarning(() => {
    if (!label && !ariaLabel && !ariaLabelledBy) {
      console.warn('NumberField requires one of label / aria-label / aria-labelledby for accessibility');
    }
  }, [label, ariaLabel, ariaLabelledBy]);

  const messages = useLocalizedMessages('numberField');

  useImperativeHandle(
    apiRef,
    () => ({
      focus: () => inputRef.current?.focus(),
      select: () => inputRef.current?.select(),
    }),
    [],
  );

  return (
    <RACNumberField
      {...props}
      ref={ref}
      className={clsx('cim-number-field', UNSAFE_className)}
      style={UNSAFE_style}
      // NumberField uses `NaN` to signify an empty value, which is unintuitive
      // https://github.com/adobe/react-spectrum/issues/5524
      // https://github.com/adobe/react-spectrum/issues/6971
      value={value === null ? Number.NaN : value}
      defaultValue={defaultValue === null ? Number.NaN : defaultValue}
      onChange={onChange ? (value) => onChange(Number.isNaN(value) ? null : value) : undefined}
    >
      <FormFieldLabel isRequired={props.isRequired} isDisabled={props.isDisabled}>
        {label}
      </FormFieldLabel>
      <RACGroup className="cim-number-field-input-group">
        <RACInput
          ref={inputRef}
          className={clsx('cim-number-field-input', textStyle({ variant: 'body' }))}
          placeholder={placeholder}
        />
        {!UNSTABLE_hideSpinner && (
          <div className="cim-number-field-spinner">
            <IconButton
              slot="decrement"
              icon={<IconSubtract />}
              aria-label={messages.format('decrement')}
              variant="tertiary"
              size="small"
            />
            <div className="cim-number-field-spinner-separator" />
            <IconButton
              slot="increment"
              icon={<IconAdd />}
              aria-label={messages.format('increment')}
              variant="tertiary"
              size="small"
            />
          </div>
        )}
      </RACGroup>
      <FormFieldError>{errorMessage}</FormFieldError>
      <FormFieldDescription>{description}</FormFieldDescription>
    </RACNumberField>
  );
}

/**
 * Allows users to edit a number with a keyboard or increment/decrement buttons.
 *
 * See [number field usage guidelines](https://ui.cimpress.io/components/number-field/).
 */
const _NumberField = withStyleProps(forwardRef(NumberField), 'NumberField');

export { _NumberField as NumberField };
